import React, { useState } from "react";
import { Box, Image, Heading, useBreakpointValue } from "@chakra-ui/react";
import { APP_CONSTANTS } from "../constants";

interface BookCardProps {
  id?: string;
  title: string;
  coverUrl?: string;
  width: string | number;
  height: string | number;
  maxHeight?: string | number;
  borderRadius?: string;
  boxShadow?: string;
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down";
}

/**
 * BookCard - A shared component for displaying book covers with consistent styling
 * Used in both Library and ReadBook components
 */
const BookCard: React.FC<BookCardProps> = ({
  id,
  title,
  coverUrl,
  width,
  height,
  maxHeight,
  borderRadius = "md",
  boxShadow = "md",
  objectFit = "cover",
}) => {
  // Fixed path to fallback image
  const fallbackImagePath = `${process.env.PUBLIC_URL}/data/fall_back.jpg`;
  const [imageError, setImageError] = useState(false);

  // Check if the coverUrl is missing, empty, or invalid
  const isCoverMissing = !coverUrl || coverUrl.trim() === "" || imageError;

  // Handle image loading error
  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <Box
      id={id}
      position="relative"
      width={width}
      mx="auto" // Center it
      height={height}
      maxHeight={maxHeight}
      borderRadius={borderRadius}
      overflow="hidden"
      boxShadow={boxShadow}
    >
      <Image
        src={isCoverMissing ? fallbackImagePath : coverUrl}
        alt={title}
        width="100%"
        height="100%"
        objectFit={objectFit}
        fallbackSrc={fallbackImagePath}
        onError={handleImageError}
      />

      {/* Always show title overlay for fallback image */}
      {isCoverMissing && (
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="rgba(0, 0, 0, 0.3)"
          p={4}
          borderRadius={borderRadius}
        >
          <Heading
            size="md"
            color="white"
            textAlign="center"
            noOfLines={3}
            textShadow="0px 2px 4px rgba(0, 0, 0, 0.9)"
            fontSize="1.2rem"
            fontWeight="bold"
            letterSpacing="0.5px"
          >
            {title}
          </Heading>
        </Box>
      )}
    </Box>
  );
};

export default BookCard;
